import { useState } from 'react';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@lms/ui';
import type { Material } from '@lms/shared/types';
import { useDeleteMaterial, useMaterialDeletionImpact } from '../hooks/useMaterials';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface MaterialDeleteActionProps {
  material: Pick<Material, 'id' | 'title'>;
  programId: string;
  onDeleted: (materialId: string) => void;
}

export function MaterialDeleteAction({
  material,
  programId,
  onDeleted,
}: MaterialDeleteActionProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const {
    data: impact,
    isPending: isCheckingImpact,
    isError: isImpactError,
  } = useMaterialDeletionImpact(material.id, isOpen);
  const { mutate: deleteMaterial, isPending: isDeleting, error } = useDeleteMaterial(programId);

  const requiresTypedConfirmation = impact?.requiresConfirmation ?? false;
  const isConfirmed = !requiresTypedConfirmation || confirmation.trim() === 'HAPUS';

  const closeDialog = () => {
    if (isDeleting) return;
    setIsOpen(false);
    setConfirmation('');
  };

  const handleDelete = () => {
    if (!isConfirmed) return;
    deleteMaterial(
      {
        id: material.id,
        confirmation: requiresTypedConfirmation ? confirmation.trim() : undefined,
      },
      {
        onSuccess: () => {
          onDeleted(material.id);
          setIsOpen(false);
          setConfirmation('');
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => (open ? setIsOpen(true) : closeDialog())}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-error-container px-3 text-xs font-label font-bold text-on-error-container transition-colors hover:bg-error hover:text-on-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
        aria-label={`${t(copy.materialModal.deleteMaterial)}: ${material.title}`}
      >
        <Trash2 size={16} aria-hidden="true" />
        <Bilingual {...copy.materialModal.deleteMaterial} />
      </button>

      <DialogContent
        overlayClassName="z-[80]"
        className="z-[81] max-h-[85vh] max-w-lg overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            <Bilingual {...copy.materialModal.deleteMaterialTitle} />
          </DialogTitle>
          <DialogDescription>
            <Bilingual {...copy.materialModal.deleteMaterialDescription} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl bg-surface-container-low p-4">
            <p className="m-0 text-sm font-label font-bold text-on-surface">{material.title}</p>
          </div>

          {isCheckingImpact && (
            <div className="flex items-center gap-3 rounded-2xl bg-surface-container-low p-4 text-sm text-on-surface-variant">
              <Loader2 size={18} className="animate-spin text-primary" aria-hidden="true" />
              <Bilingual {...copy.materialModal.deleteCheckingImpact} />
            </div>
          )}

          {isImpactError && (
            <div
              role="alert"
              className="flex gap-3 rounded-2xl bg-error-container p-4 text-sm text-on-error-container"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
              <Bilingual {...copy.materialModal.deleteImpactError} />
            </div>
          )}

          {impact && (
            <div className="space-y-3 rounded-2xl bg-warning-container p-4 text-on-warning-container">
              <p className="m-0 text-xs font-label font-bold uppercase tracking-wider">
                <Bilingual {...copy.materialModal.deleteImpactTitle} />
              </p>
              <ul className="m-0 space-y-1.5 pl-5 text-sm leading-relaxed">
                <li>
                  {t(copy.materialModal.deleteImpactMedia).replace(
                    '{count}',
                    String(impact.mediaCount)
                  )}
                </li>
                <li>
                  {t(copy.materialModal.deleteImpactEvaluations).replace(
                    '{count}',
                    String(impact.evaluationCount)
                  )}
                </li>
                <li>
                  {t(copy.materialModal.deleteImpactCompletions).replace(
                    '{count}',
                    String(impact.completionCount)
                  )}
                </li>
                <li>
                  {t(copy.materialModal.deleteImpactAttempts).replace(
                    '{count}',
                    String(impact.evaluationAttemptCount)
                  )}
                </li>
                <li>
                  {t(copy.materialModal.deleteImpactReflections).replace(
                    '{count}',
                    String(impact.reflectionCount)
                  )}
                </li>
              </ul>
            </div>
          )}

          {requiresTypedConfirmation && (
            <div className="space-y-2 rounded-2xl bg-error-container p-4">
              <p className="m-0 text-sm font-label font-bold text-on-error-container">
                <Bilingual {...copy.materialModal.deleteConfirmationRequired} />
              </p>
              <label
                htmlFor="material-delete-confirmation"
                className="block text-xs font-label font-bold text-on-error-container"
              >
                <Bilingual {...copy.materialModal.deleteConfirmationLabel} />
              </label>
              <input
                id="material-delete-confirmation"
                value={confirmation}
                onChange={event => setConfirmation(event.target.value)}
                placeholder={t(copy.materialModal.deleteConfirmationPlaceholder)}
                autoComplete="off"
                className="h-11 w-full rounded-xl bg-surface-container-lowest px-3 text-sm font-body text-on-surface outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              />
            </div>
          )}

          {error && (
            <p role="alert" className="m-0 text-sm text-error">
              {error instanceof Error ? error.message : t(copy.materialModal.deleteErrorFallback)}
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={closeDialog} disabled={isDeleting}>
            <Bilingual {...copy.materialModal.abortBtn} />
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={isCheckingImpact || isImpactError || !impact || !isConfirmed || isDeleting}
            className="bg-error text-on-error hover:bg-error/90"
          >
            {isDeleting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            <Bilingual {...copy.materialModal.deleteMaterial} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
